import { IMAGES_CDN_URL, PLAGUE_ENABLED } from "@/config/constants";
import GameError, { GameErrorType } from "@/libs/GameError";
import {
	AudioNames,
	AudioSpriteNames,
} from "./entities/config/audio/audioConfig";
import type MainMapScene from "./MainMapScene";

type AudioType = AudioNames | AudioSpriteNames;

interface AudioConfig {
	type: "audio" | "audioSprite";
	jsonPath?: string;
	paths: Array<string>;
	loop?: boolean;
}

/**
 * AudioLazyLoader - Manages lazy loading of audio assets
 *
 * This service optimizes initial load time by:
 * 1. Only loading critical audio (UI sounds, main music) at startup
 * 2. Loading cutscene and intro audio on-demand when needed
 * 3. Preventing duplicate loads with caching and promise deduplication
 * 4. Providing centralized audio configuration
 */
export class AudioLazyLoader {
	private static instance: AudioLazyLoader | null = null;
	private scene: MainMapScene | null = null;

	// Track ongoing load operations to prevent duplicate requests
	private readonly loadingPromises: Map<AudioType, Promise<void>> = new Map();

	// Cache of successfully loaded audio assets
	private readonly loadedAudio: Set<AudioType> = new Set();

	private constructor() {
		// Private constructor enforces singleton pattern
	}

	public static getInstance(): AudioLazyLoader {
		if (!this.instance) {
			this.instance = new AudioLazyLoader();
		}
		return this.instance;
	}

	/**
	 * Initialize the loader with a Phaser scene
	 * Must be called before loading any audio
	 */
	public setScene(scene: MainMapScene): void {
		this.scene = scene;
	}

	/**
	 * Check if audio asset is already loaded in Phaser or our cache
	 */
	public isLoaded(audioName: AudioType): boolean {
		return (
			this.loadedAudio.has(audioName) ||
			!!this.scene?.sound.get(audioName)
		);
	}

	/**
	 * Check if audio asset is currently being loaded
	 */
	public isLoading(audioName: AudioType): boolean {
		return this.loadingPromises.has(audioName);
	}

	/**
	 * Lazy load audio asset
	 * Returns immediately if already loaded or currently loading
	 *
	 * @param audioName - The audio asset to load
	 * @returns Promise that resolves when audio is loaded
	 */
	public async loadAudio(audioName: AudioType): Promise<void> {
		// Return early if already loaded
		if (this.isLoaded(audioName)) {
			return;
		}

		// Return existing promise if currently loading (deduplication)
		if (this.loadingPromises.has(audioName)) {
			return this.loadingPromises.get(audioName);
		}

		// Validate scene is set
		if (!this.scene) {
			console.error(
				`AudioLazyLoader: Cannot load "${audioName}" - scene not initialized`,
			);
			return;
		}

		// Start new load operation
		const loadPromise = this.performLoad(audioName);
		this.loadingPromises.set(audioName, loadPromise);

		try {
			await loadPromise;
			this.loadedAudio.add(audioName);
		} catch (error) {
			console.error(
				`AudioLazyLoader: Failed to load "${audioName}"`,
				error,
			);
		} finally {
			// Clean up loading promise regardless of success/failure
			this.loadingPromises.delete(audioName);
		}
	}

	/**
	 * Perform the actual audio loading operation using Phaser's loader
	 */
	private async performLoad(audioName: AudioType): Promise<void> {
		if (!this.scene) {
			throw new Error("Scene not initialized");
		}

		const config = this.getAudioConfig(audioName);
		if (!config) {
			throw new Error(`No configuration found for audio: ${audioName}`);
		}

		return new Promise<void>(
			(resolve: CallableFunction, reject: CallableFunction): void => {
				const loader = this.scene!.load;

				const onComplete = (): void => {
					this.cleanupListeners(loader, onComplete, onError);
					resolve();
				};

				const onError = (file: Phaser.Loader.File): void => {
					this.cleanupListeners(loader, onComplete, onError);
					reject(
						new GameError(
							GameErrorType.FE_ERROR,
							`Failed to load audio asset: ${audioName}`,
							{ file: file.key, url: file.url },
						),
					);
				};

				// Register event listeners
				loader.once("complete", onComplete);
				loader.once("loaderror", onError);

				// Add audio to Phaser loader based on type
				this.addAudioToLoader(loader, audioName, config);

				// Start loading if loader is idle
				if (!loader.isLoading()) {
					loader.start();
				}
			},
		);
	}

	/**
	 * Add audio to Phaser's loader based on configuration type
	 */
	private addAudioToLoader(
		loader: Phaser.Loader.LoaderPlugin,
		audioName: AudioType,
		config: AudioConfig,
	): void {
		if (config.type === "audioSprite" && config.jsonPath) {
			loader.audioSprite(audioName, config.jsonPath, config.paths);
		} else if (config.type === "audio") {
			const options = config.loop ? { loop: true } : undefined;
			loader.audio(audioName, config.paths, options);
		}
	}

	/**
	 * Clean up Phaser loader event listeners
	 */
	private cleanupListeners(
		loader: Phaser.Loader.LoaderPlugin,
		onComplete: () => void,
		onError: (file: Phaser.Loader.File) => void,
	): void {
		loader.off("complete", onComplete);
		loader.off("loaderror", onError);
	}

	/**
	 * Get audio configuration for a given audio asset
	 * Centralizes all audio file paths and settings
	 */
	private getAudioConfig(audioName: AudioType): AudioConfig | null {
		const AUDIO_FORMATS = {
			ui: (base: string): Array<string> => [
				`${IMAGES_CDN_URL}/${base}.ogg`,
				`${IMAGES_CDN_URL}/${base}.ac3`,
				`${IMAGES_CDN_URL}/${base}.mp3`,
				`${IMAGES_CDN_URL}/${base}.m4a`,
			],
			music: (base: string): Array<string> => [
				`${IMAGES_CDN_URL}/${base}.ogg`,
				`${IMAGES_CDN_URL}/${base}.ac3`,
				`${IMAGES_CDN_URL}/${base}.m4a`,
				`${IMAGES_CDN_URL}/${base}.mp3`,
			],
		};
		const plagueFolder = PLAGUE_ENABLED ? "plague/" : "";
		const configs: Record<AudioType, AudioConfig> = {
			// UI Audio Sprite
			[AudioSpriteNames.UI]: {
				type: "audioSprite",
				jsonPath: `${IMAGES_CDN_URL}/sounds/ui-sounds.json`,
				paths: AUDIO_FORMATS.ui("sounds/ui-sounds"),
			},

			// Music Tracks
			[AudioNames.MAIN_MUSIC]: {
				type: "audio",
				paths: AUDIO_FORMATS.music("sounds/main/main-music"),
				loop: true,
			},
			[AudioNames.INTRO_MUSIC]: {
				type: "audio",
				paths: AUDIO_FORMATS.music("sounds/intro/intro"),
				loop: true,
			},

			// Cutscene Audio Sprites
			[AudioSpriteNames.CUTSCENES_INTRO]: {
				type: "audioSprite",
				jsonPath: `${IMAGES_CDN_URL}/sounds/cutscenes/${plagueFolder}cutscenes.json`,
				paths: AUDIO_FORMATS.ui(
					`sounds/cutscenes/${plagueFolder}cutscenes`,
				),
			},
			[AudioSpriteNames.CUTSCENES_OUTRO_CHAPTER_1]: {
				type: "audioSprite",
				jsonPath: `${IMAGES_CDN_URL}/sounds/outro/outro.json`,
				paths: AUDIO_FORMATS.ui("sounds/outro/outro"),
			},
			[AudioSpriteNames.CUTSCENES_OUTRO_CHAPTER_2]: {
				type: "audioSprite",
				jsonPath: `${IMAGES_CDN_URL}/sounds/cutscenes-3/cutscenes-3.json`,
				paths: AUDIO_FORMATS.ui("sounds/cutscenes-3/cutscenes-3"),
			},
			[AudioSpriteNames.CUTSCENES_OUTRO_CHAPTER_3]: {
				type: "audioSprite",
				jsonPath: `${IMAGES_CDN_URL}/sounds/cutscenes-4/cutscenes-4.json`,
				paths: AUDIO_FORMATS.ui("sounds/cutscenes-4/cutscenes-4"),
			},
			[AudioSpriteNames.FX1]: {
				type: "audioSprite",
				jsonPath: `${IMAGES_CDN_URL}/sounds/fx1/cutscenes.json`,
				paths: AUDIO_FORMATS.ui("sounds/fx1/cutscenes"),
			},
			[AudioNames.CITY_AMBIENT]: {
				type: "audio",
				paths: AUDIO_FORMATS.music("sounds/city-ambient/city-ambient"),
				loop: true,
			},
		};

		return configs[audioName] ?? null;
	}

	/**
	 * Preload multiple audio assets in parallel
	 *
	 * @param audioNames - Array of audio assets to load
	 * @returns Promise that resolves when all assets are loaded
	 */
	public async loadMultiple(audioNames: Array<AudioType>): Promise<void> {
		await Promise.all(
			audioNames.map(
				(name: AudioType): Promise<void> => this.loadAudio(name),
			),
		);
	}

	/**
	 * Clear all cached audio data
	 * Useful for testing or memory management
	 */
	public clearCache(): void {
		this.loadedAudio.clear();
		this.loadingPromises.clear();
	}

	/**
	 * Get loading statistics for debugging
	 */
	public getStats(): {
		loaded: Array<string>;
		loading: Array<string>;
		total: number;
	} {
		return {
			loaded: Array.from(this.loadedAudio),
			loading: Array.from(this.loadingPromises.keys()),
			total: this.loadedAudio.size + this.loadingPromises.size,
		};
	}
}
